import { motion } from "framer-motion";
import { useEffect, useState } from "react";

export const Gallery = () => {
  const images = [
    "/gallery/43f9848e-c07f-4ec1-aa8e-8bf0ac6e65ac_20260102_212006_0000.jpg",
    "/gallery/4f4d993f-b958-4a20-95dc-f2094d69b8da_20260102_213129_0000.jpg",
    "/gallery/IMG-20200107-WA0047.jpg",
    "/gallery/IMG-20200213-WA0034.jpg",
    "/gallery/IMG-20200213-WA0037.jpg",
    "/gallery/IMG-20200217-WA0002.jpg",
    "/gallery/IMG-20240313-WA0010.jpg",
    "/gallery/IMG20230409114533.jpg",
    "/gallery/IMG20240210103116.jpg",
    "/gallery/IMG_20230529_142859.jpg",
    "/gallery/IMG_20230810_181216.jpg",
  ];

  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  useEffect(() => {
    if (activeIndex === null) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setActiveIndex(null);
      if (event.key === "ArrowRight") {
        setActiveIndex((prev) => (prev === null ? 0 : (prev + 1) % images.length));
      }
      if (event.key === "ArrowLeft") {
        setActiveIndex((prev) => (prev === null ? 0 : (prev - 1 + images.length) % images.length));
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeIndex, images.length]);

  return (
    <div className="w-full bg-white">
      <section className="relative overflow-hidden bg-linear-to-br from-orange-50 via-white to-orange-100">
        <div className="max-w-6xl mx-auto px-4 py-20 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-gray-900"
          >
            Gallery
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-gray-700 mt-4 max-w-3xl mx-auto"
          >
            Moments from our screen-free coding sessions, hands-on challenges, and joyful learning.
          </motion.p>
        </div>
      </section>

      <section className="py-16 px-4">
        <div
          className="max-w-6xl mx-auto select-none"
          onContextMenu={(event) => event.preventDefault()}
          onDragStart={(event) => event.preventDefault()}
        >
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6">
            {images.map((src, index) => (
              <motion.div
                key={src}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.03 }}
                className="mb-6 break-inside-avoid"
              >
                <button
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className="rounded-2xl overflow-hidden shadow-md border border-gray-100 bg-white w-full text-left"
                >
                  <img
                    src={src}
                    alt={`Gallery image ${index + 1}`}
                    loading="lazy"
                    draggable={false}
                    onContextMenu={(event) => event.preventDefault()}
                    onDragStart={(event) => event.preventDefault()}
                    style={{ WebkitTouchCallout: "none" }}
                    className="w-full h-auto object-cover"
                  />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {activeIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setActiveIndex(null);
            }
          }}
          onContextMenu={(event) => event.preventDefault()}
        >
          <button
            type="button"
            onClick={() => setActiveIndex(null)}
            className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/90 text-gray-900 font-bold shadow-md"
            aria-label="Close image preview"
          >
            ✕
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setActiveIndex((prev) => (prev === null ? 0 : (prev - 1 + images.length) % images.length));
            }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 text-gray-900 font-bold shadow-md"
            aria-label="Previous image"
          >
            ←
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setActiveIndex((prev) => (prev === null ? 0 : (prev + 1) % images.length));
            }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 text-gray-900 font-bold shadow-md"
            aria-label="Next image"
          >
            →
          </button>
          <div
            className="max-w-5xl w-full"
            onClick={(event) => event.stopPropagation()}
            onContextMenu={(event) => event.preventDefault()}
          >
            <img
              src={images[activeIndex]}
              alt="Expanded gallery"
              className="w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl"
              draggable={false}
              onContextMenu={(event) => event.preventDefault()}
              onDragStart={(event) => event.preventDefault()}
              style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
